import {
  ComponentProps,
  createContext,
  createSignal,
  useContext,
} from "solid-js";
import { Event } from "solid-u";

import UObject from "@repo/utils/UObject";
import UPromise from "@repo/utils/UPromise";

import LoadableButton from "../buttons/LoadableButton";
import { useError } from "../error/ErrorProvider";
import createForm from "../signals/createForm";

declare namespace Form {
  type Props = Omit<ComponentProps<"form">, "onSubmit"> & {
    onSubmit(
      e: Event.FromHandlerUnion<ComponentProps<"form">["onSubmit"]> & {
        setPendingText(text?: string): void;
      }
    ): Promise<any> | void;
  };
}

function Form(props: Form.Props) {
  const [status, setStatus] = createSignal<LoadableButton.Status>("initial");
  const [pendingText, setPendingText] = createSignal<string>();
  const error = useError();

  return (
    <FormContext.Provider value={{ status, pendingText }}>
      <form
        {...props}
        onSubmit={async (e) => {
          e.preventDefault();
          if (status() !== "initial") return;

          try {
            const res = props.onSubmit(Object.assign(e, { setPendingText }));
            if (!(res instanceof Promise)) return;

            setStatus("pending");

            await res;
            setStatus("success");
            await UPromise.delay(LoadableButton.successDuration);
          } catch (e) {
            if (e instanceof createForm.ValidationError) return;
            if (e == null) return;
            error.capture(e);
          } finally {
            setStatus("initial");
            setPendingText();
          }
        }}
      >
        {props.children}
      </form>
    </FormContext.Provider>
  );
}

Form.Submit = <As extends LoadableButton.As>(
  props: UObject.PartialBy<
    Omit<LoadableButton.Props<As>, "status" | "type">,
    "onClick"
  >
) => {
  const form = useContext(FormContext);
  if (!form) throw new Error("Form.Submit must be rendered as child of Form.");

  return (
    // @ts-expect-error
    <LoadableButton
      {...props}
      text={{
        ...props.text,
        pending: form.pendingText() || props.text?.pending,
      }}
      type="submit"
      status={form.status()}
    >
      {props.children}
    </LoadableButton>
  );
};

export default Form;

const FormContext = createContext<{
  status: () => LoadableButton.Status;
  pendingText: () => string | undefined;
}>();
